import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AccountProfile, Gender, SecurityAnswer } from "@gooner/shared";
import {
  PASSWORD_MIN,
  SECURITY_ANSWER_COUNT,
  SECURITY_QUESTIONS,
  dobError,
  emailError,
  passwordError,
  securityAnswerError,
} from "@gooner/shared";
import { userApi } from "../../lib/api";
import { usePublicSettings } from "../../lib/publicSettings";
import { useSession } from "../../lib/session";
import { track } from "../../analytics";
import { AuthedPage } from "../components/AuthedPage";
import { PatreonCard } from "../components/PatreonCard";
import {
  DobField,
  EMAIL_NEVER_NOTE,
  FieldNote,
  GenderField,
  SecurityQuestionRow,
} from "../components/accountFields";
import { Avatar } from "../ui/Avatar";
import { Button, Field, TextInput } from "../ui/primitives";
import { MASK } from "../../lib/replayMask";

/**
 * Account settings.
 *
 * The home for the four facts sign-up started asking for on 2026-09-20. Every account
 * older than that lands here with blanks, and blank is a legitimate permanent state:
 * nothing on this page pushes, and nothing anywhere is gated on filling it in. The one
 * exception (2026-10-25) is a dismissible line for missing security questions
 * (SecurityQuestionsNudge), because password recovery runs on them now.
 *
 * Two forms with two Save buttons rather than one page-wide Save. They post to
 * different shapes — details is a partial patch of three nullable columns, the
 * questions are an all-or-nothing replacement of a triple — so one button would have to
 * define "dirty" across both and decide what half a failure looks like. The profile
 * page's avatar dialog and visibility toggle already save one thing at a time.
 *
 * NOT to be confused with /api/settings, which is the public system-settings read. This
 * page is the account's; that one is the server's.
 */
export function Settings() {
  return (
    <AuthedPage title="Settings">
      <SettingsContent />
    </AuthedPage>
  );
}

/** The strip-the-status-prefix idiom every error surface here uses. */
const clean = (e: unknown) =>
  e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "Something went wrong.";

function SettingsContent() {
  const user = useSession((s) => s.user)!;
  const minAge = usePublicSettings()?.ageGate?.minAge ?? 18;
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    userApi
      .accountProfile()
      .then((r) => !cancelled && setProfile(r.profile))
      .catch((e: unknown) => !cancelled && setLoadError(clean(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) {
    return <p className="text-sm text-red-400">{loadError}</p>;
  }
  if (!profile) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-zinc-100">Settings</h1>

      <section className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
        <div className="flex items-center gap-3">
          <Avatar name={user.username} src={user.avatarUrl} />
          <div>
            <p className="text-sm font-semibold text-zinc-100" {...MASK}>{user.username}</p>
            <p className="text-xs text-zinc-500">
              Member since {new Date(user.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </section>

      <DetailsSection profile={profile} minAge={minAge} onSaved={setProfile} />
      <SecuritySection profile={profile} onSaved={setProfile} />
      <PasswordSection />
      <PatreonCard />
      <DeleteAccountSection />
    </div>
  );
}

/** Save -> "Saved" for a moment, the SavePill vocabulary without the pill. */
function useSaved() {
  const [savedAt, setSavedAt] = useState(0);
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(0), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);
  return [savedAt > 0, () => setSavedAt(Date.now())] as const;
}

function DetailsSection({
  profile,
  minAge,
  onSaved,
}: {
  profile: AccountProfile;
  minAge: number;
  onSaved: (p: AccountProfile) => void;
}) {
  const [email, setEmail] = useState(profile.email ?? "");
  const [dob, setDob] = useState(profile.dob ?? "");
  const [gender, setGender] = useState<Gender | "">(profile.gender ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, markSaved] = useSaved();

  const dirty =
    email !== (profile.email ?? "") ||
    (!profile.dobLocked && dob !== (profile.dob ?? "")) ||
    gender !== (profile.gender ?? "");

  async function save() {
    if (busy) return;
    setError(null);
    // Validate with the same shared helpers the server uses, so its answer can never
    // surprise the form.
    if (email) {
      const err = emailError(email);
      if (err) return setError(err);
    }
    if (!profile.dobLocked && dob) {
      const err = dobError(dob, Date.now(), minAge);
      if (err) return setError(err);
    }
    setBusy(true);
    try {
      // Only what changed. An untouched field is absent, not sent as an empty string —
      // the door tells the two apart.
      const patch: Record<string, unknown> = {};
      if (email !== (profile.email ?? "")) patch.email = email;
      if (!profile.dobLocked && dob && dob !== (profile.dob ?? "")) patch.dob = dob;
      if (gender && gender !== (profile.gender ?? "")) patch.gender = gender;
      const r = await userApi.updateAccountProfile(patch);
      onSaved(r.profile);
      markSaved();
      track("settings_saved", { section: "details" });
    } catch (e: unknown) {
      setError(clean(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
      <h2 className="text-sm font-semibold text-zinc-100">Your details</h2>

      <div>
        <Field label="Email">
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
          />
        </Field>
        <FieldNote>{EMAIL_NEVER_NOTE}</FieldNote>
      </div>

      {/*
       * Once a birthday is set it is rendered as TEXT, not as a disabled input. A
       * disabled field still looks operable and still shows a picker glyph in some
       * browsers; there is nothing to operate, so nothing operable is rendered. The
       * lock itself lives in the UPDATE's WHERE clause on the server — this is the
       * affordance, never the enforcement.
       */}
      {profile.dobLocked ? (
        <div>
          <Field label="Date of birth">
            {/* A date of birth identifies a person: masked in replay, like the field it replaces. */}
            <p className="rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] px-3 py-2 text-sm text-zinc-300" {...MASK}>
              {profile.dob}
            </p>
          </Field>
          <FieldNote>Your date of birth is set once. Get in touch if it&rsquo;s wrong.</FieldNote>
        </div>
      ) : (
        <div>
          <DobField value={dob} onChange={setDob} minAge={minAge} />
          <FieldNote>You can only set this once, so check it before you save.</FieldNote>
        </div>
      )}

      <GenderField value={gender} onChange={setGender} />

      <div className="flex items-center gap-3">
        <Button variant="primary" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? "…" : saved ? "Saved" : "Save"}
        </Button>
        {error ? (
          <p className="text-xs text-red-400" aria-live="polite">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function SecuritySection({
  profile,
  onSaved,
}: {
  profile: AccountProfile;
  onSaved: (p: AccountProfile) => void;
}) {
  const existing = profile.securityQuestionIds;
  const [questions, setQuestions] = useState<string[]>(() =>
    Array.from({ length: SECURITY_ANSWER_COUNT }, (_, i) => existing[i] ?? ""),
  );
  const [answers, setAnswers] = useState<string[]>(() =>
    Array.from({ length: SECURITY_ANSWER_COUNT }, () => ""),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, markSaved] = useSaved();

  const ready = useMemo(
    () =>
      questions.every(Boolean) &&
      new Set(questions).size === SECURITY_ANSWER_COUNT &&
      answers.every((a) => a.trim()),
    [questions, answers],
  );

  const setAt = (list: string[], i: number, v: string) =>
    list.map((old, idx) => (idx === i ? v : old));

  async function save() {
    if (busy) return;
    setError(null);
    for (const a of answers) {
      const err = securityAnswerError(a);
      if (err) return setError(err);
    }
    setBusy(true);
    try {
      const security: SecurityAnswer[] = questions.map((questionId, i) => ({
        questionId,
        answer: answers[i]!,
      }));
      const r = await userApi.updateAccountProfile({ security });
      onSaved(r.profile);
      setAnswers(Array.from({ length: SECURITY_ANSWER_COUNT }, () => ""));
      markSaved();
      track("settings_saved", { section: "security" });
    } catch (e: unknown) {
      setError(clean(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
      <h2 className="text-sm font-semibold text-zinc-100">Security questions</h2>
      {/*
       * The answers are WRITE-ONLY. The server returns question ids and nothing else,
       * which is what keeps the answers off every DTO — so these boxes start empty even
       * for an account that has them set, and the copy says so rather than letting it
       * read as a bug. It also makes the section all-or-nothing by construction: there
       * is no way to change one answer without retyping the other two, and we are not
       * going to show them in order to make that possible.
       */}
      <p className="text-xs leading-snug text-zinc-400">
        {existing.length
          ? "Your answers aren't shown here. To change them, pick your three questions and type all three answers again."
          : `You haven't set any yet. ${SECURITY_ANSWER_COUNT} questions are how you get back in if you forget your password.`}
      </p>
      {existing.length ? (
        <ul className="space-y-1 text-xs text-zinc-500">
          {existing.map((id) => (
            <li key={id}>· {SECURITY_QUESTIONS.find((q) => q.id === id)?.prompt ?? id}</li>
          ))}
        </ul>
      ) : null}

      <div className="space-y-2">
        {questions.map((q, i) => (
          <SecurityQuestionRow
            key={i}
            index={i}
            questionId={q}
            answer={answers[i] ?? ""}
            taken={questions}
            onQuestion={(id) => setQuestions((prev) => setAt(prev, i, id))}
            onAnswer={(text) => setAnswers((prev) => setAt(prev, i, text))}
          />
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="primary" onClick={() => void save()} disabled={busy || !ready}>
          {busy ? "…" : saved ? "Saved" : existing.length ? "Replace answers" : "Save"}
        </Button>
        {error ? (
          <p className="text-xs text-red-400" aria-live="polite">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Change the password (2026-10-25). Needs the current one — a session left open on a
 * shared screen must not be enough to lock its owner out. Every OTHER session is signed
 * out by the server; this one stays.
 */
function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, markSaved] = useSaved();

  async function save() {
    if (busy) return;
    setError(null);
    const err = passwordError(password, confirm);
    if (err) return setError(err);
    setBusy(true);
    try {
      await userApi.changePassword({ current, password, passwordConfirm: confirm });
      setCurrent("");
      setPassword("");
      setConfirm("");
      markSaved();
      track("settings_saved", { section: "password" });
    } catch (e: unknown) {
      setError(clean(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
      <h2 className="text-sm font-semibold text-zinc-100">Password</h2>
      <p className="text-xs leading-snug text-zinc-400">
        Changing it signs you out everywhere else.
      </p>
      <Field label="Current password">
        <TextInput type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </Field>
      <div>
        <Field label="New password">
          <TextInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <FieldNote tone={password && password.length >= PASSWORD_MIN ? "ok" : undefined}>
          At least {PASSWORD_MIN} characters.
        </FieldNote>
      </div>
      <div>
        <Field label="Confirm new password">
          <TextInput type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
        <FieldNote tone={!confirm ? undefined : password === confirm ? "ok" : "bad"}>
          {!confirm ? "" : password === confirm ? "Passwords match." : "Passwords don't match."}
        </FieldNote>
      </div>
      <div className="flex items-center gap-3">
        <Button variant="primary" onClick={() => void save()} disabled={busy || !current || !password || !confirm}>
          {busy ? "…" : saved ? "Saved" : "Change password"}
        </Button>
        {error ? (
          <p className="text-xs text-red-400" aria-live="polite">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Delete the account (2026-10-28). Until this, leaving meant emailing us. Collapsed to
 * one button until asked, so the page's last section is not a password box anybody
 * types into by accident; opened, it says what goes and asks for the password, which is
 * the actual gate (the server checks it, like PasswordSection's current password).
 */
function DeleteAccountSection() {
  const deleteAccount = useSession((s) => s.deleteAccount);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await deleteAccount(password);
      navigate("/", { replace: true });
    } catch (e: unknown) {
      setError(clean(e));
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-xl border border-red-500/30 bg-[var(--color-panel)] p-4">
      <h2 className="text-sm font-semibold text-zinc-100">Delete account</h2>
      {!open ? (
        <>
          <p className="text-xs leading-snug text-zinc-400">
            Permanently remove your account and everything saved to it.
          </p>
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete account…
          </Button>
        </>
      ) : (
        <>
          <p className="text-xs leading-snug text-zinc-400">
            This can&rsquo;t be undone. Deleting removes your profile, collections, saved views and screens, vault
            uploads, posts and comments, faps, Gold and watch history. Your username becomes free for someone else.
          </p>
          <p className="text-xs leading-snug text-zinc-400">
            If you support us on Patreon, cancel the pledge on Patreon too. Deleting here doesn&rsquo;t stop it.
          </p>
          <Field label="Password">
            <TextInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="danger" onClick={() => void confirm()} disabled={busy || !password}>
              {busy ? "Deleting…" : "Delete my account permanently"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setPassword("");
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
          {error ? (
            <p className="text-xs text-red-400" aria-live="polite">
              {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
